import { useState, useEffect, useCallback } from "react";
import { 
  Inbox, Trash2, Phone, Mail, MessageCircle, Building2, Calendar,
  FileText, Plus, DollarSign, Send, Search, Filter, Eye, Edit,
  MapPin, Target, Gauge, ClipboardCheck, MoreHorizontal, ArrowRight,
  CheckCircle2, AlertTriangle, AlertCircle, Clock, Users, Copy, Link, CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

// Estados del prospecto en el pipeline
const STATUS_CONFIG = {
  pending: { label: "Nuevo", color: "bg-gray-100 text-gray-700", order: 1 },
  contacted: { label: "Contactado", color: "bg-blue-100 text-blue-700", order: 2 },
  in_progress: { label: "En Proceso", color: "bg-purple-100 text-purple-700", order: 3 },
  diagnostic_pending: { label: "Diag. Pendiente", color: "bg-orange-100 text-orange-700", order: 4 },
  diagnostic_sent: { label: "Diag. Recibido", color: "bg-amber-100 text-amber-700", order: 5 },
  completed: { label: "Completado", color: "bg-emerald-100 text-emerald-700", order: 6 },
};

// Colores para el Kanban
const KANBAN_COLORS = {
  pending: { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-300", dot: "bg-gray-500" },
  contacted: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-300", dot: "bg-blue-500" },
  in_progress: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-300", dot: "bg-purple-500" },
  diagnostic_pending: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-300", dot: "bg-orange-500" },
  diagnostic_sent: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-300", dot: "bg-amber-500" },
  completed: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-300", dot: "bg-emerald-500" },
};

// Catálogos para el formulario extendido
const ORIGENES_PROSPECTO = [
  { id: "formulario_web", label: "Formulario Web" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "llamada", label: "Llamada" },
  { id: "referido", label: "Referido" },
  { id: "campaña_digital", label: "Campaña Digital" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "evento", label: "Evento" },
  { id: "cliente_existente", label: "Cliente Existente" },
  { id: "captura_manual", label: "Captura Manual" },
  { id: "otro", label: "Otro" }
];

const TIPOS_CLIENTE = [
  { id: "autoconsumo", label: "Usuario Final de Autoconsumo" },
  { id: "transportista", label: "Transportista" },
  { id: "agricola", label: "Agrícola" },
  { id: "minera", label: "Minera" },
  { id: "constructora", label: "Constructora" },
  { id: "industrial", label: "Industrial" },
  { id: "gasolinera_bandera_blanca", label: "Gasolinera Bandera Blanca" },
  { id: "comercializadora", label: "Comercializadora" },
  { id: "distribuidor", label: "Distribuidor" },
  { id: "almacenista", label: "Almacenista" },
  { id: "otro", label: "Otro" }
];

// ═══════════════════════════════════════════════════════════════
// PLANTILLAS DE DIAGNÓSTICO POR TIPO DE SERVICIO
// ═══════════════════════════════════════════════════════════════

const PLANTILLAS_DIAGNOSTICO = {
  "Permisos de Autoconsumo": {
    nombre: "Diagnóstico para Permisos de Autoconsumo",
    descripcion: "Evaluación para tramitar o regularizar permisos de autoconsumo de combustible ante la CRE",
    preguntas: [
      { id: "almacena_combustible", pregunta: "¿La empresa almacena combustible para consumo propio?", categoria: "operacion", critica: true },
      { id: "volumen_almacenamiento", pregunta: "¿El volumen de almacenamiento supera los 1,000 litros?", categoria: "operacion", critica: true },
      { id: "tiene_tanque_fijo", pregunta: "¿Cuenta con tanque de almacenamiento fijo?", categoria: "infraestructura", critica: true },
      { id: "tanque_predio_propio", pregunta: "¿El tanque está instalado en predio propio o arrendado?", categoria: "infraestructura" },
      { id: "capacidad_tanque", pregunta: "¿Conoce la capacidad exacta del tanque (litros)?", categoria: "infraestructura" },
      { id: "documentacion_tanque", pregunta: "¿Tiene documentación del tanque (factura, ficha técnica)?", categoria: "documentacion" },
      { id: "tiene_memoria_tecnica", pregunta: "¿Cuenta con memoria técnica de la instalación?", categoria: "documentacion" },
      { id: "tiene_planos", pregunta: "¿Tiene planos de la instalación?", categoria: "documentacion" },
      { id: "tiene_dictamen_tecnico", pregunta: "¿Cuenta con dictamen o responsiva técnica vigente?", categoria: "documentacion" },
      { id: "tiene_permiso_cre", pregunta: "¿Actualmente tiene permiso de autoconsumo de la CRE?", categoria: "permisos", critica: true },
      { id: "permiso_vigente", pregunta: "Si tiene permiso, ¿está vigente?", categoria: "permisos" },
      { id: "tiene_proteccion_civil", pregunta: "¿Cuenta con dictamen de Protección Civil?", categoria: "permisos" },
      { id: "tiene_uso_suelo", pregunta: "¿Cuenta con licencia de uso de suelo compatible?", categoria: "permisos" },
      { id: "documentacion_legal_predio", pregunta: "¿Tiene documentación legal del predio (escrituras/contrato)?", categoria: "legal" },
      { id: "constancia_fiscal", pregunta: "¿Tiene constancia de situación fiscal actualizada?", categoria: "fiscal" },
      { id: "recibio_inspeccion", pregunta: "¿Ha recibido inspección de ASEA, CRE o Profeco?", categoria: "cumplimiento" },
      { id: "tiene_requerimiento", pregunta: "¿Tiene algún requerimiento o multa pendiente?", categoria: "cumplimiento", critica: true },
      { id: "requiere_urgente", pregunta: "¿Requiere regularización urgente (menos de 30 días)?", categoria: "urgencia" }
    ],
    servicios_sugeridos: ["Permiso de Autoconsumo CRE", "Regularización Documental", "Memoria Técnica", "Dictamen Protección Civil"]
  },
  
  "Controles Volumétricos": {
    nombre: "Diagnóstico para Controles Volumétricos",
    descripcion: "Evaluación para implementar o regularizar controles volumétricos según lineamientos del SAT",
    preguntas: [
      { id: "tiene_permiso", pregunta: "¿Cuenta con permiso de la CRE vigente?", categoria: "permisos", critica: true },
      { id: "tipo_actividad", pregunta: "¿Qué actividad realiza? (Almacenamiento, distribución, venta)", categoria: "operacion" },
      { id: "tiene_controles_vol", pregunta: "¿Actualmente cuenta con sistema de controles volumétricos?", categoria: "controles", critica: true },
      { id: "tipo_sistema_cv", pregunta: "Si tiene, ¿qué tipo de sistema usa (automático/manual)?", categoria: "controles" },
      { id: "tiene_proveedor_cv", pregunta: "¿Cuenta con proveedor autorizado de controles volumétricos?", categoria: "controles" },
      { id: "genera_reportes_sat", pregunta: "¿Genera y envía reportes mensuales al SAT?", categoria: "controles", critica: true },
      { id: "tiene_bitacoras", pregunta: "¿Lleva bitácoras de operación?", categoria: "controles" },
      { id: "tiene_sellos_sat", pregunta: "¿Cuenta con sellos electrónicos del SAT?", categoria: "controles" },
      { id: "dictamen_laboratorio", pregunta: "¿Tiene dictamen de laboratorio de pruebas?", categoria: "documentacion" },
      { id: "calibracion_equipos", pregunta: "¿Los equipos de medición están calibrados?", categoria: "infraestructura" },
      { id: "conectividad_internet", pregunta: "¿El sitio cuenta con conectividad a internet estable?", categoria: "infraestructura" },
      { id: "personal_capacitado", pregunta: "¿Tiene personal capacitado para operar el sistema?", categoria: "operacion" },
      { id: "recibio_auditoria_sat", pregunta: "¿Ha recibido auditoría del SAT relacionada a CV?", categoria: "cumplimiento" },
      { id: "tiene_requerimiento_sat", pregunta: "¿Tiene requerimiento pendiente del SAT?", categoria: "cumplimiento", critica: true },
      { id: "fecha_limite_sat", pregunta: "¿Tiene fecha límite para cumplir con el SAT?", categoria: "urgencia" },
      { id: "requiere_urgente", pregunta: "¿Requiere implementación urgente (menos de 30 días)?", categoria: "urgencia" }
    ],
    servicios_sugeridos: ["Controles Volumétricos SAT", "Proveedor Autorizado CV", "Capacitación Personal", "Auditoría de Cumplimiento"]
  },
  
  "Gestión Integral": {
    nombre: "Diagnóstico para Gestión Integral",
    descripcion: "Evaluación completa para gestión integral de cumplimiento regulatorio en hidrocarburos",
    preguntas: [
      { id: "almacena_combustible", pregunta: "¿La empresa almacena combustible?", categoria: "operacion", critica: true },
      { id: "vende_terceros", pregunta: "¿Vende combustible a terceros?", categoria: "operacion" },
      { id: "tiene_tanque_fijo", pregunta: "¿Cuenta con tanque de almacenamiento?", categoria: "infraestructura", critica: true },
      { id: "capacidad_total", pregunta: "¿Cuál es la capacidad total de almacenamiento?", categoria: "infraestructura" },
      { id: "tiene_permiso_cre", pregunta: "¿Tiene permiso de la CRE?", categoria: "permisos", critica: true },
      { id: "permiso_vigente", pregunta: "¿El permiso está vigente?", categoria: "permisos" },
      { id: "tiene_controles_vol", pregunta: "¿Cuenta con controles volumétricos?", categoria: "controles", critica: true },
      { id: "genera_reportes", pregunta: "¿Genera reportes al SAT?", categoria: "controles" },
      { id: "tiene_proteccion_civil", pregunta: "¿Tiene dictamen de Protección Civil?", categoria: "permisos" },
      { id: "tiene_uso_suelo", pregunta: "¿Cuenta con uso de suelo?", categoria: "permisos" },
      { id: "tiene_impacto_ambiental", pregunta: "¿Tiene manifestación de impacto ambiental?", categoria: "permisos" },
      { id: "documentacion_completa", pregunta: "¿La documentación técnica está completa?", categoria: "documentacion" },
      { id: "polizas_seguro", pregunta: "¿Cuenta con pólizas de seguro vigentes?", categoria: "legal" },
      { id: "constancia_fiscal", pregunta: "¿Tiene constancia fiscal actualizada?", categoria: "fiscal" },
      { id: "recibio_inspeccion", pregunta: "¿Ha recibido inspecciones de autoridades?", categoria: "cumplimiento" },
      { id: "tiene_requerimiento", pregunta: "¿Tiene requerimientos pendientes?", categoria: "cumplimiento", critica: true },
      { id: "necesita_capacitacion", pregunta: "¿El personal necesita capacitación?", categoria: "operacion" },
      { id: "requiere_urgente", pregunta: "¿Requiere atención urgente?", categoria: "urgencia" }
    ],
    servicios_sugeridos: ["Permiso de Autoconsumo CRE", "Controles Volumétricos SAT", "Gestión ASEA", "Protección Civil", "Servicio Mensual Recurrente"]
  },
  
  "Suministro de Combustible": {
    nombre: "Diagnóstico para Suministro de Combustible",
    descripcion: "Evaluación para clientes que requieren suministro y logística de combustible",
    preguntas: [
      { id: "tipo_combustible", pregunta: "¿Qué tipo de combustible requiere (Diésel, Gasolina, Gas LP)?", categoria: "operacion", critica: true },
      { id: "consumo_mensual", pregunta: "¿Cuál es su consumo mensual aproximado (litros)?", categoria: "operacion", critica: true },
      { id: "tiene_tanque", pregunta: "¿Cuenta con tanque de almacenamiento propio?", categoria: "infraestructura" },
      { id: "capacidad_tanque", pregunta: "¿Cuál es la capacidad del tanque?", categoria: "infraestructura" },
      { id: "ubicacion_entrega", pregunta: "¿La ubicación de entrega es accesible para pipas?", categoria: "logistica" },
      { id: "horario_entrega", pregunta: "¿Tiene restricciones de horario para entregas?", categoria: "logistica" },
      { id: "frecuencia_entrega", pregunta: "¿Con qué frecuencia requiere entregas?", categoria: "logistica" },
      { id: "tiene_permiso", pregunta: "¿Cuenta con permiso de autoconsumo?", categoria: "permisos" },
      { id: "facturacion_requerida", pregunta: "¿Requiere facturación fiscal?", categoria: "fiscal", critica: true },
      { id: "forma_pago", pregunta: "¿Cuál sería su forma de pago preferida?", categoria: "comercial" },
      { id: "credito_requerido", pregunta: "¿Requiere línea de crédito?", categoria: "comercial" },
      { id: "proveedor_actual", pregunta: "¿Actualmente tiene proveedor de combustible?", categoria: "comercial" },
      { id: "problema_actual", pregunta: "¿Tiene algún problema con su proveedor actual?", categoria: "comercial" },
      { id: "urgencia_suministro", pregunta: "¿Requiere suministro urgente?", categoria: "urgencia" }
    ],
    servicios_sugeridos: ["Suministro de Combustible", "Logística de Entregas", "Línea de Crédito", "Permiso de Autoconsumo"]
  }
};

// Preguntas genéricas (fallback si no hay plantilla específica)
const PREGUNTAS_GENERICAS = [
  { id: "almacena_combustible", pregunta: "¿La empresa almacena combustible para consumo propio?", categoria: "operacion" },
  { id: "vende_terceros", pregunta: "¿El combustible se vende a terceros?", categoria: "operacion" },
  { id: "tiene_tanque_fijo", pregunta: "¿Cuenta con tanque fijo?", categoria: "infraestructura" },
  { id: "tiene_permiso", pregunta: "¿Tiene permiso de la CRE?", categoria: "permisos" },
  { id: "tiene_controles_vol", pregunta: "¿Cuenta con controles volumétricos?", categoria: "controles" },
  { id: "tiene_requerimiento", pregunta: "¿Tiene requerimiento pendiente?", categoria: "cumplimiento" },
  { id: "requiere_urgente", pregunta: "¿Requiere atención urgente?", categoria: "urgencia" }
];

// Función para obtener preguntas según el tipo de servicio
const getPreguntasPorServicio = (tipoServicio) => {
  const plantilla = PLANTILLAS_DIAGNOSTICO[tipoServicio];
  return plantilla ? plantilla.preguntas : PREGUNTAS_GENERICAS;
};

// Función para obtener servicios sugeridos según el tipo de servicio
const getServiciosSugeridos = (tipoServicio) => {
  const plantilla = PLANTILLAS_DIAGNOSTICO[tipoServicio];
  return plantilla ? plantilla.servicios_sugeridos : [];
};

export default function ProspectosTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [view, setView] = useState("kanban"); // kanban | lista
  const [userRole, setUserRole] = useState("admin");
  
  // Modales
  const [newProspectOpen, setNewProspectOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [diagnosticoModalOpen, setDiagnosticoModalOpen] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [diagnosticoLinkModal, setDiagnosticoLinkModal] = useState(false);
  const [diagnosticoLink, setDiagnosticoLink] = useState("");
  
  // Prospecto seleccionado
  const [selectedProspect, setSelectedProspect] = useState(null);
  
  // Formulario nuevo prospecto (extendido)
  const [newProspectForm, setNewProspectForm] = useState({
    // Datos de la Empresa
    company_name: "",
    rfc: "",
    giro_empresa: "",
    // Datos de Contacto
    full_name: "",
    puesto: "",
    phone: "",
    whatsapp: "",
    email: "",
    // Ubicación
    estado: "",
    municipio: "",
    // Clasificación
    origen: "captura_manual",
    tipo_cliente: "autoconsumo",
    service_type: "",
    // Información Operativa
    tiene_tanque: "no_sabe",
    tiene_permiso: "no_sabe",
    tiene_controles_volumetricos: "no_sabe",
    volumen_mensual: 0,
    // Comentarios
    description: "",
  });
  const [creatingProspect, setCreatingProspect] = useState(false);

  // Formulario diagnóstico
  const [diagnosticoForm, setDiagnosticoForm] = useState({
    respuestas: {},
    observaciones: "",
    servicios_recomendados: [],
    siguiente_accion: "",
  });
  const [savingDiagnostico, setSavingDiagnostico] = useState(false);

  // Cotización
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [quoteForm, setQuoteForm] = useState({
    items: [],
    notes: "",
    validity_days: 7,
  });
  const [newItem, setNewItem] = useState({
    service_id: "",
    service_name: "",
    description: "",
    quantity: 1,
    unit_price: 0,
  });
  const [creatingQuote, setCreatingQuote] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [prospectsRes, meRes] = await Promise.all([
        axios.get(`${API}/admin/diagnostic-requests/without-quotation`, axCfg),
        axios.get(`${API}/admin/me`, axCfg)
      ]);
      setData(prospectsRes.data);
      setUserRole(meRes.data?.role || "admin");
    } catch (e) {
      toast.error("Error al cargar prospectos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchServiceCatalog = async () => {
    try {
      const res = await axios.get(`${API}/admin/service-catalog`, axCfg);
      setServiceCatalog(res.data);
    } catch (e) {
      console.error("Error loading service catalog");
    }
  };

  // Cambiar estado del prospecto
  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/diagnostic-requests/${id}`, { status }, axCfg);
      toast.success("Estado actualizado");
      fetchData();
    } catch (e) {
      toast.error("Error al actualizar");
    }
  };

  // Eliminar prospecto
  const deleteRequest = async (id) => {
    if (!window.confirm("¿Eliminar este prospecto?")) return;
    try {
      await axios.delete(`${API}/admin/diagnostic-requests/${id}`, axCfg);
      toast.success("Prospecto eliminado");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  // Abrir WhatsApp
  const sendWhatsApp = (prospect) => {
    const phone = (prospect.whatsapp || prospect.phone || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("El prospecto no tiene número de teléfono");
      return;
    }
    const msg = `Hola ${prospect.full_name}, gracias por tu interés en PETROCONTROL. Recibí tu solicitud sobre "${prospect.service_type}". ¿Podemos agendar una llamada para darte más información?`;
    window.open(`https://wa.me/52${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // Enviar email (simulado - abre cliente de correo)
  const sendEmail = (prospect) => {
    if (!prospect.email) {
      toast.error("El prospecto no tiene email");
      return;
    }
    const subject = encodeURIComponent(`PETROCONTROL - Información sobre ${prospect.service_type}`);
    const body = encodeURIComponent(`Estimado/a ${prospect.full_name},\n\nGracias por su interés en los servicios de PETROCONTROL.\n\nQuedamos a sus órdenes.\n\nSaludos cordiales,\nEquipo PETROCONTROL`);
    window.open(`mailto:${prospect.email}?subject=${subject}&body=${body}`, "_blank");
  };

  // Crear prospecto
  const createProspect = async () => {
    if (!newProspectForm.company_name.trim()) {
      toast.error("La razón social / nombre de empresa es requerido");
      return;
    }
    if (!newProspectForm.full_name.trim()) {
      toast.error("El nombre del contacto es requerido");
      return;
    }
    if (!newProspectForm.phone.trim()) {
      toast.error("El teléfono es requerido");
      return;
    }

    setCreatingProspect(true);
    try {
      await axios.post(`${API}/admin/prospectos`, {
        ...newProspectForm,
        // Mapear campos al formato esperado por el backend
        email: newProspectForm.email || `prospecto_${Date.now()}@temp.com`,
      }, axCfg);
      toast.success("Prospecto creado exitosamente");
      setNewProspectOpen(false);
      resetProspectForm();
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let errorMsg = "Error al crear prospecto";
      if (typeof detail === "string") errorMsg = detail;
      toast.error(errorMsg);
    } finally {
      setCreatingProspect(false);
    }
  };

  const resetProspectForm = () => {
    setNewProspectForm({
      company_name: "", rfc: "", giro_empresa: "",
      full_name: "", puesto: "", phone: "", whatsapp: "", email: "",
      estado: "", municipio: "",
      origen: "captura_manual", tipo_cliente: "autoconsumo", service_type: "",
      tiene_tanque: "no_sabe", tiene_permiso: "no_sabe", tiene_controles_volumetricos: "no_sabe",
      volumen_mensual: 0, description: "",
    });
  };

  // Habilitar diagnóstico para que el cliente lo llene
  const habilitarDiagnosticoCliente = async (prospect) => {
    try {
      const res = await axios.post(`${API}/admin/diagnostic-requests/${prospect.id}/habilitar-diagnostico`, {}, axCfg);
      const fullLink = `${window.location.origin}/diagnostico/${res.data.diagnostico_token}`;
      setDiagnosticoLink(fullLink);
      setSelectedProspect(prospect);
      setDiagnosticoLinkModal(true);
      toast.success("Diagnóstico habilitado. Comparta el link con el cliente.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al habilitar diagnóstico");
    }
  };

  // Copiar link al portapapeles
  const copiarLinkDiagnostico = () => {
    navigator.clipboard.writeText(diagnosticoLink);
    toast.success("Link copiado al portapapeles");
  };

  // Enviar link por WhatsApp
  const enviarLinkWhatsApp = () => {
    if (!selectedProspect) return;
    const phone = (selectedProspect.whatsapp || selectedProspect.phone || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("El prospecto no tiene número de teléfono");
      return;
    }
    const msg = `*PETROCONTROL - Diagnóstico Regulatorio*\n\nEstimado/a ${selectedProspect.full_name},\n\nPara brindarte una mejor atención, por favor completa el siguiente formulario de diagnóstico:\n\n${diagnosticoLink}\n\nEste formulario nos ayudará a entender tus necesidades y ofrecerte una solución personalizada.\n\nQuedamos a tus órdenes.\n\n_Equipo PETROCONTROL_`;
    window.open(`https://wa.me/52${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // Abrir modal de diagnóstico (para que admin llene manualmente)
  const openDiagnosticoModal = (prospect) => {
    setSelectedProspect(prospect);
    setDiagnosticoForm({
      respuestas: prospect.diagnostico_respuestas || {},
      observaciones: prospect.diagnostico_observaciones || "",
      servicios_recomendados: prospect.diagnostico_servicios || [],
      siguiente_accion: prospect.diagnostico_siguiente_accion || "",
    });
    setDiagnosticoModalOpen(true);
  };

  // Guardar diagnóstico
  const saveDiagnostico = async () => {
    if (!selectedProspect) return;
    setSavingDiagnostico(true);
    try {
      await axios.patch(`${API}/admin/diagnostic-requests/${selectedProspect.id}`, {
        diagnostico_respuestas: diagnosticoForm.respuestas,
        diagnostico_observaciones: diagnosticoForm.observaciones,
        diagnostico_servicios: diagnosticoForm.servicios_recomendados,
        diagnostico_siguiente_accion: diagnosticoForm.siguiente_accion,
        diagnostico_completado: true,
      }, axCfg);
      toast.success("Diagnóstico guardado");
      setDiagnosticoModalOpen(false);
      fetchData();
    } catch (e) {
      toast.error("Error al guardar diagnóstico");
    } finally {
      setSavingDiagnostico(false);
    }
  };

  // Enviar diagnóstico por WhatsApp
  const enviarDiagnosticoWhatsApp = async () => {
    if (!selectedProspect) return;
    
    // Primero guardar
    await saveDiagnostico();
    
    const phone = (selectedProspect.whatsapp || selectedProspect.phone || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("El prospecto no tiene número de teléfono");
      return;
    }
    
    // Construir mensaje del diagnóstico
    const serviciosTexto = diagnosticoForm.servicios_recomendados.length > 0 
      ? `\n\n*Servicios recomendados:*\n${diagnosticoForm.servicios_recomendados.join("\n- ")}`
      : "";
    
    const msg = `*DIAGNÓSTICO REGULATORIO PETROCONTROL*\n\nEstimado/a ${selectedProspect.full_name},\n\nHemos realizado el diagnóstico regulatorio para ${selectedProspect.company_name || "su empresa"}.\n\n*Observaciones:*\n${diagnosticoForm.observaciones || "Sin observaciones"}${serviciosTexto}\n\n*Siguiente paso:* ${diagnosticoForm.siguiente_accion || "Por definir"}\n\nQuedamos a sus órdenes.\n\n_Equipo PETROCONTROL_`;
    
    window.open(`https://wa.me/52${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    
    // Actualizar estado a "diagnostic_sent"
    await updateStatus(selectedProspect.id, "diagnostic_sent");
  };

  // Enviar diagnóstico por email
  const enviarDiagnosticoEmail = async () => {
    if (!selectedProspect) return;
    
    await saveDiagnostico();
    
    if (!selectedProspect.email) {
      toast.error("El prospecto no tiene email");
      return;
    }
    
    const serviciosTexto = diagnosticoForm.servicios_recomendados.length > 0 
      ? `\n\nServicios recomendados:\n- ${diagnosticoForm.servicios_recomendados.join("\n- ")}`
      : "";
    
    const subject = encodeURIComponent(`PETROCONTROL - Diagnóstico Regulatorio`);
    const body = encodeURIComponent(`Estimado/a ${selectedProspect.full_name},\n\nHemos realizado el diagnóstico regulatorio para ${selectedProspect.company_name || "su empresa"}.\n\nObservaciones:\n${diagnosticoForm.observaciones || "Sin observaciones"}${serviciosTexto}\n\nSiguiente paso: ${diagnosticoForm.siguiente_accion || "Por definir"}\n\nQuedamos a sus órdenes.\n\nEquipo PETROCONTROL`);
    
    window.open(`mailto:${selectedProspect.email}?subject=${subject}&body=${body}`, "_blank");
    
    await updateStatus(selectedProspect.id, "diagnostic_sent");
  };

  // Cotización
  const openQuoteModal = async (prospect) => {
    setSelectedProspect(prospect);
    setQuoteForm({ items: [], notes: "", validity_days: 7 });
    setNewItem({ service_id: "", service_name: "", description: "", quantity: 1, unit_price: 0 });
    await fetchServiceCatalog();
    setQuoteModalOpen(true);
  };

  const handleServiceSelect = (serviceId) => {
    const service = serviceCatalog.find(s => s.id === serviceId);
    if (service) {
      setNewItem({
        service_id: service.id,
        service_name: service.name,
        description: service.description,
        quantity: 1,
        unit_price: service.price,
      });
    }
  };

  const addItemToQuote = () => {
    if (!newItem.service_name || newItem.unit_price <= 0) {
      toast.error("Completa los datos del servicio");
      return;
    }
    setQuoteForm({
      ...quoteForm,
      items: [...quoteForm.items, { ...newItem }],
    });
    setNewItem({ service_id: "", service_name: "", description: "", quantity: 1, unit_price: 0 });
  };

  const removeItemFromQuote = (index) => {
    setQuoteForm({
      ...quoteForm,
      items: quoteForm.items.filter((_, i) => i !== index),
    });
  };

  const calculateTotals = () => {
    const subtotal = quoteForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const iva = subtotal * 0.16;
    const total = subtotal + iva;
    return { subtotal, iva, total };
  };

  const createQuotation = async () => {
    if (quoteForm.items.length === 0) {
      toast.error("Agrega al menos un servicio");
      return;
    }

    setCreatingQuote(true);
    try {
      await axios.post(`${API}/admin/quotations`, {
        prospect_id: selectedProspect.id,
        items: quoteForm.items,
        notes: quoteForm.notes,
        validity_days: quoteForm.validity_days,
      }, axCfg);
      
      toast.success("Cotización creada. El prospecto pasará a la pestaña Cotizaciones.");
      setQuoteModalOpen(false);
      setQuoteForm({ items: [], notes: "", validity_days: 7 });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear cotización");
    } finally {
      setCreatingQuote(false);
    }
  };

  // Filtrar datos
  const filteredData = data.filter(s => {
    const matchSearch = !search || 
      s.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.email?.toLowerCase().includes(search.toLowerCase()) ||
      s.phone?.includes(search);
    const matchStatus = filterStatus === "all" || s.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // Agrupar por estado para Kanban
  const prospectosPorEstado = {};
  Object.keys(STATUS_CONFIG).forEach(status => {
    prospectosPorEstado[status] = filteredData.filter(p => p.status === status);
  });

  const { subtotal, iva, total } = calculateTotals();

  if (loading) return <p className="text-center py-12 text-gray-500">Cargando prospectos...</p>;

  return (
    <div className="space-y-4" data-testid="prospectos-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre, empresa, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-prospectos"
            />
          </div>
        </div>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setView("kanban")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              view === "kanban" ? "bg-white text-[#1a5c4a] shadow-sm" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Kanban
          </button>
          <button
            onClick={() => setView("lista")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              view === "lista" ? "bg-white text-[#1a5c4a] shadow-sm" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Lista
          </button>
        </div>

        <span className="text-sm text-gray-500">{filteredData.length} prospectos</span>

        <Button
          onClick={() => setNewProspectOpen(true)}
          className="bg-[#1a5c4a] hover:bg-[#143f34]"
          data-testid="new-prospect-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Prospecto
        </Button>
      </div>

      {/* Vista Kanban */}
      {view === "kanban" && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {Object.entries(STATUS_CONFIG).map(([status, cfg]) => {
              const statusProspectos = prospectosPorEstado[status] || [];
              const colors = KANBAN_COLORS[status];
              
              return (
                <div key={status} className="w-[300px] flex-shrink-0">
                  <div className={`rounded-t-xl px-3 py-2 ${colors.bg} border-b-2 ${colors.border}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                        <span className={`font-semibold text-sm ${colors.text}`}>{cfg.label}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">{statusProspectos.length}</Badge>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-b-xl min-h-[400px] p-2 space-y-2">
                    {statusProspectos.map(prospect => (
                      <ProspectoCard
                        key={prospect.id}
                        prospect={prospect}
                        onWhatsApp={() => sendWhatsApp(prospect)}
                        onEmail={() => sendEmail(prospect)}
                        onDiagnostico={() => openDiagnosticoModal(prospect)}
                        onCotizar={() => openQuoteModal(prospect)}
                        onChangeStatus={(newStatus) => updateStatus(prospect.id, newStatus)}
                        onDelete={() => deleteRequest(prospect.id)}
                        userRole={userRole}
                      />
                    ))}
                    
                    {statusProspectos.length === 0 && (
                      <div className="text-center py-8 text-gray-400 text-sm">
                        Sin prospectos
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vista Lista */}
      {view === "lista" && (
        <Card className="border-none shadow-sm">
          <CardContent className="p-0">
            {filteredData.length === 0 ? (
              <div className="text-center py-16">
                <Inbox className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay prospectos {search ? "con ese criterio" : "registrados"}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">CONTACTO</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">EMPRESA</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">SERVICIO</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">ESTADO</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">FECHA</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredData.map(prospect => {
                      const statusCfg = STATUS_CONFIG[prospect.status] || STATUS_CONFIG.pending;
                      
                      return (
                        <tr key={prospect.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-sm text-gray-900">{prospect.full_name}</p>
                              <p className="text-xs text-gray-500">{prospect.email}</p>
                              <p className="text-xs text-gray-500">{prospect.phone}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-900">{prospect.company_name || "-"}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-[#1a5c4a] font-medium">{prospect.service_type}</p>
                          </td>
                          <td className="px-4 py-3">
                            <Select value={prospect.status} onValueChange={(v) => updateStatus(prospect.id, v)}>
                              <SelectTrigger className="w-[150px] h-8">
                                <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-gray-500">
                              {new Date(prospect.created_at).toLocaleDateString('es-MX')}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {!prospect.diagnostico_habilitado && !prospect.diagnostico_completado_cliente && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-amber-600 border-amber-300"
                                  onClick={() => habilitarDiagnosticoCliente(prospect)}
                                  data-testid="habilitar-diagnostico-btn"
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  Habilitar Diag.
                                </Button>
                              )}
                              {prospect.diagnostico_habilitado && !prospect.diagnostico_completado_cliente && (
                                <Badge className="bg-amber-100 text-amber-700">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Esperando
                                </Badge>
                              )}
                              {prospect.diagnostico_completado_cliente && (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Recibido
                                </Badge>
                              )}
                              <Button size="sm" variant="outline" onClick={() => openDiagnosticoModal(prospect)}>
                                <ClipboardCheck className="h-4 w-4 mr-1" />
                                Ver/Editar
                              </Button>
                              <Button size="sm" className="bg-[#1a5c4a] hover:bg-[#164a3d]" onClick={() => openQuoteModal(prospect)}>
                                <FileText className="h-4 w-4 mr-1" />
                                Cotizar
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => sendWhatsApp(prospect)}>
                                <MessageCircle className="h-4 w-4 text-green-600" />
                              </Button>
                              {userRole === "admin" && (
                                <Button size="sm" variant="ghost" onClick={() => deleteRequest(prospect.id)}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal Nuevo Prospecto (Extendido) */}
      <Dialog open={newProspectOpen} onOpenChange={setNewProspectOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#1a5c4a]" />
              Nuevo Prospecto
            </DialogTitle>
            <DialogDescription>
              Capture la información del nuevo prospecto para iniciar el proceso comercial
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Datos de la Empresa */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Datos de la Empresa
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Razón Social / Nombre de Empresa *</Label>
                  <Input
                    value={newProspectForm.company_name}
                    onChange={(e) => setNewProspectForm({...newProspectForm, company_name: e.target.value})}
                    placeholder="Empresa S.A. de C.V."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>RFC</Label>
                    <Input
                      value={newProspectForm.rfc}
                      onChange={(e) => setNewProspectForm({...newProspectForm, rfc: e.target.value.toUpperCase()})}
                      placeholder="ABC123456789"
                      maxLength={13}
                    />
                  </div>
                  <div>
                    <Label>Giro de la empresa</Label>
                    <Input
                      value={newProspectForm.giro_empresa}
                      onChange={(e) => setNewProspectForm({...newProspectForm, giro_empresa: e.target.value})}
                      placeholder="Transporte, Agricultura, etc."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Datos de Contacto */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                <Phone className="h-4 w-4" /> Datos de Contacto
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre del contacto *</Label>
                  <Input
                    value={newProspectForm.full_name}
                    onChange={(e) => setNewProspectForm({...newProspectForm, full_name: e.target.value})}
                    placeholder="Juan Pérez"
                  />
                </div>
                <div>
                  <Label>Puesto</Label>
                  <Input
                    value={newProspectForm.puesto}
                    onChange={(e) => setNewProspectForm({...newProspectForm, puesto: e.target.value})}
                    placeholder="Gerente de Operaciones"
                  />
                </div>
                <div>
                  <Label>Teléfono *</Label>
                  <Input
                    value={newProspectForm.phone}
                    onChange={(e) => setNewProspectForm({...newProspectForm, phone: e.target.value})}
                    placeholder="33 1234 5678"
                  />
                </div>
                <div>
                  <Label>WhatsApp</Label>
                  <Input
                    value={newProspectForm.whatsapp}
                    onChange={(e) => setNewProspectForm({...newProspectForm, whatsapp: e.target.value})}
                    placeholder="33 1234 5678"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newProspectForm.email}
                    onChange={(e) => setNewProspectForm({...newProspectForm, email: e.target.value})}
                    placeholder="contacto@empresa.com"
                  />
                </div>
              </div>
            </div>

            {/* Ubicación */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Ubicación
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Estado</Label>
                  <Input
                    value={newProspectForm.estado}
                    onChange={(e) => setNewProspectForm({...newProspectForm, estado: e.target.value})}
                    placeholder="Jalisco"
                  />
                </div>
                <div>
                  <Label>Municipio</Label>
                  <Input
                    value={newProspectForm.municipio}
                    onChange={(e) => setNewProspectForm({...newProspectForm, municipio: e.target.value})}
                    placeholder="Guadalajara"
                  />
                </div>
              </div>
            </div>

            {/* Clasificación */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                <Target className="h-4 w-4" /> Clasificación
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Origen del prospecto</Label>
                  <Select 
                    value={newProspectForm.origen}
                    onValueChange={(v) => setNewProspectForm({...newProspectForm, origen: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ORIGENES_PROSPECTO.map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo de cliente</Label>
                  <Select 
                    value={newProspectForm.tipo_cliente}
                    onValueChange={(v) => setNewProspectForm({...newProspectForm, tipo_cliente: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_CLIENTE.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Tipo de Servicio *</Label>
                  <Select
                    value={newProspectForm.service_type}
                    onValueChange={(v) => setNewProspectForm({...newProspectForm, service_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar servicio..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Permisos de Autoconsumo">Permisos de Autoconsumo</SelectItem>
                      <SelectItem value="Controles Volumétricos">Controles Volumétricos</SelectItem>
                      <SelectItem value="Gestión Integral">Gestión Integral</SelectItem>
                      <SelectItem value="Suministro de Combustible">Suministro de Combustible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Información Operativa */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                <Gauge className="h-4 w-4" /> Información Operativa
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>¿Tiene tanque?</Label>
                  <Select 
                    value={newProspectForm.tiene_tanque}
                    onValueChange={(v) => setNewProspectForm({...newProspectForm, tiene_tanque: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="no_sabe">No sabe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>¿Tiene permiso?</Label>
                  <Select 
                    value={newProspectForm.tiene_permiso}
                    onValueChange={(v) => setNewProspectForm({...newProspectForm, tiene_permiso: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="en_tramite">En trámite</SelectItem>
                      <SelectItem value="no_sabe">No sabe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>¿Tiene controles volumétricos?</Label>
                  <Select 
                    value={newProspectForm.tiene_controles_volumetricos}
                    onValueChange={(v) => setNewProspectForm({...newProspectForm, tiene_controles_volumetricos: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="parcial">Parcial</SelectItem>
                      <SelectItem value="no_sabe">No sabe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Volumen mensual (litros)</Label>
                  <Input
                    type="number"
                    value={newProspectForm.volumen_mensual}
                    onChange={(e) => setNewProspectForm({...newProspectForm, volumen_mensual: parseInt(e.target.value) || 0})}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Comentarios */}
            <div>
              <Label>Comentarios adicionales</Label>
              <Textarea
                value={newProspectForm.description}
                onChange={(e) => setNewProspectForm({...newProspectForm, description: e.target.value})}
                placeholder="Información adicional relevante..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewProspectOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={createProspect} 
              disabled={creatingProspect}
              className="bg-[#1a5c4a] hover:bg-[#143f34]"
            >
              {creatingProspect ? "Creando..." : "Crear Prospecto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Diagnóstico */}
      <Dialog open={diagnosticoModalOpen} onOpenChange={setDiagnosticoModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-violet-600" />
              Diagnóstico Regulatorio
            </DialogTitle>
            <DialogDescription>
              {selectedProspect?.full_name} | {selectedProspect?.company_name}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="preguntas" className="mt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="preguntas">Preguntas</TabsTrigger>
              <TabsTrigger value="servicios">Servicios</TabsTrigger>
              <TabsTrigger value="resultado">Resultado</TabsTrigger>
            </TabsList>
            
            <TabsContent value="preguntas" className="space-y-4 mt-4">
              {/* Información de la plantilla */}
              {selectedProspect?.service_type && PLANTILLAS_DIAGNOSTICO[selectedProspect.service_type] && (
                <div className="bg-[#1a5c4a]/5 border border-[#1a5c4a]/20 rounded-lg p-3 mb-4">
                  <h4 className="font-semibold text-sm text-[#1a5c4a]">
                    {PLANTILLAS_DIAGNOSTICO[selectedProspect.service_type].nombre}
                  </h4>
                  <p className="text-xs text-gray-600 mt-1">
                    {PLANTILLAS_DIAGNOSTICO[selectedProspect.service_type].descripcion}
                  </p>
                </div>
              )}
              
              <p className="text-sm text-gray-600 mb-4">
                Responda las siguientes preguntas para determinar los servicios recomendados.
                {selectedProspect?.service_type && (
                  <span className="ml-1 text-[#1a5c4a] font-medium">
                    (Plantilla: {selectedProspect.service_type})
                  </span>
                )}
              </p>
              
              <ScrollArea className="h-[300px] pr-4">
                {getPreguntasPorServicio(selectedProspect?.service_type).map(p => (
                  <div key={p.id} className={`flex items-center justify-between py-2 border-b border-gray-100 ${p.critica ? 'bg-red-50/50' : ''}`}>
                    <div className="flex-1 pr-4">
                      <span className="text-sm text-gray-700">{p.pregunta}</span>
                      {p.critica && (
                        <Badge className="ml-2 text-[10px] bg-red-100 text-red-700">Crítica</Badge>
                      )}
                    </div>
                    <Select 
                      value={diagnosticoForm.respuestas[p.id] || ""}
                      onValueChange={(v) => setDiagnosticoForm({
                        ...diagnosticoForm,
                        respuestas: { ...diagnosticoForm.respuestas, [p.id]: v }
                      })}
                    >
                      <SelectTrigger className="w-[100px]">
                        <SelectValue placeholder="--" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="si">Sí</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="no_sabe">No sabe</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="servicios" className="space-y-4 mt-4">
              {/* Servicios sugeridos por la plantilla */}
              {selectedProspect?.service_type && getServiciosSugeridos(selectedProspect.service_type).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-amber-700 mb-2 font-medium">Servicios sugeridos para {selectedProspect.service_type}:</p>
                  <div className="flex flex-wrap gap-1">
                    {getServiciosSugeridos(selectedProspect.service_type).map((servicio, idx) => (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className="text-xs cursor-pointer hover:bg-amber-100 border-amber-300"
                        onClick={() => {
                          if (!diagnosticoForm.servicios_recomendados.includes(servicio)) {
                            setDiagnosticoForm({
                              ...diagnosticoForm,
                              servicios_recomendados: [...diagnosticoForm.servicios_recomendados, servicio]
                            });
                          }
                        }}
                      >
                        + {servicio}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-sm text-gray-600 mb-4">
                Seleccione los servicios recomendados para este prospecto.
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                {[
                  "Permiso de Autoconsumo CRE",
                  "Controles Volumétricos SAT",
                  "Regularización Documental",
                  "Gestión ASEA",
                  "Protección Civil",
                  "Uso de Suelo",
                  "Memoria Técnica",
                  "Dictamen Técnico",
                  "Auditoría de Cumplimiento",
                  "Atención a Inspección/Requerimiento",
                  "Capacitación Personal",
                  "Suministro de Combustible",
                  "Logística de Entregas",
                  "Servicio Mensual Recurrente"
                ].map(servicio => (
                  <div key={servicio} className="flex items-center space-x-2">
                    <Checkbox 
                      id={servicio}
                      checked={diagnosticoForm.servicios_recomendados.includes(servicio)}
                      onCheckedChange={(checked) => {
                        const nuevos = checked 
                          ? [...diagnosticoForm.servicios_recomendados, servicio]
                          : diagnosticoForm.servicios_recomendados.filter(s => s !== servicio);
                        setDiagnosticoForm({ ...diagnosticoForm, servicios_recomendados: nuevos });
                      }}
                    />
                    <Label htmlFor={servicio} className="text-sm cursor-pointer">{servicio}</Label>
                  </div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="resultado" className="space-y-4 mt-4">
              <div>
                <Label>Observaciones y recomendaciones</Label>
                <Textarea 
                  value={diagnosticoForm.observaciones}
                  onChange={(e) => setDiagnosticoForm({...diagnosticoForm, observaciones: e.target.value})}
                  placeholder="Notas y observaciones del diagnóstico..."
                  rows={4}
                />
              </div>
              <div>
                <Label>Siguiente acción</Label>
                <Input 
                  value={diagnosticoForm.siguiente_accion}
                  onChange={(e) => setDiagnosticoForm({...diagnosticoForm, siguiente_accion: e.target.value})}
                  placeholder="Ej: Generar cotización y enviar propuesta"
                />
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6 flex-wrap gap-2">
            <Button variant="outline" onClick={() => setDiagnosticoModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="outline"
              onClick={saveDiagnostico}
              disabled={savingDiagnostico}
            >
              {savingDiagnostico ? "Guardando..." : "Guardar"}
            </Button>
            <Button 
              variant="outline"
              className="text-green-600 border-green-300 hover:bg-green-50"
              onClick={enviarDiagnosticoWhatsApp}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Enviar WhatsApp
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={enviarDiagnosticoEmail}
            >
              <Mail className="h-4 w-4 mr-2" />
              Enviar Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Cotización */}
      <Dialog open={quoteModalOpen} onOpenChange={setQuoteModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#1a5c4a]" />
              Nueva Cotización
            </DialogTitle>
            <DialogDescription>
              {selectedProspect?.full_name} | {selectedProspect?.company_name} | {selectedProspect?.email}
            </DialogDescription>
          </DialogHeader>

          {/* Add Service Section */}
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h4 className="font-medium text-sm mb-3">Agregar Servicio</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-xs">Servicio del Catálogo</Label>
                <Select value={newItem.service_id} onValueChange={handleServiceSelect}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar servicio..." />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceCatalog.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} - ${s.price?.toLocaleString('es-MX')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">O escribe manualmente</Label>
                <Input
                  className="mt-1"
                  placeholder="Nombre del servicio"
                  value={newItem.service_name}
                  onChange={(e) => setNewItem({ ...newItem, service_name: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs">Cantidad</Label>
                <Input
                  type="number"
                  min="1"
                  className="mt-1"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label className="text-xs">Precio Unitario</Label>
                <Input
                  type="number"
                  min="0"
                  className="mt-1"
                  value={newItem.unit_price}
                  onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-end">
                <Button type="button" onClick={addItemToQuote} className="w-full bg-[#1a5c4a] hover:bg-[#164a3d]">
                  <Plus className="h-4 w-4 mr-1" /> Agregar
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Descripción (opcional)</Label>
              <Input
                className="mt-1"
                placeholder="Descripción del servicio..."
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              />
            </div>
          </div>

          {/* Items List */}
          {quoteForm.items.length > 0 && (
            <div className="mb-4">
              <Label className="text-xs mb-2 block">Servicios en la cotización</Label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2 text-left">Servicio</th>
                      <th className="p-2 text-center">Cant.</th>
                      <th className="p-2 text-right">P.Unit</th>
                      <th className="p-2 text-right">Importe</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteForm.items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">
                          <div className="font-medium">{item.service_name}</div>
                          {item.description && <div className="text-gray-500 text-[10px]">{item.description}</div>}
                        </td>
                        <td className="p-2 text-center">{item.quantity}</td>
                        <td className="p-2 text-right">${item.unit_price.toLocaleString('es-MX')}</td>
                        <td className="p-2 text-right font-medium">${(item.quantity * item.unit_price).toLocaleString('es-MX')}</td>
                        <td className="p-2">
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeItemFromQuote(idx)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="mt-3 bg-[#1a5c4a]/5 p-3 rounded-lg">
                <div className="flex justify-between text-sm mb-1">
                  <span>Subtotal:</span>
                  <span>${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>IVA (16%):</span>
                  <span>${iva.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-[#1a5c4a]">
                  <span>TOTAL:</span>
                  <span>${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          )}

          {/* Notes and Validity */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-xs">Validez (días)</Label>
              <Select value={String(quoteForm.validity_days)} onValueChange={(v) => setQuoteForm({ ...quoteForm, validity_days: parseInt(v) })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 días</SelectItem>
                  <SelectItem value="7">7 días</SelectItem>
                  <SelectItem value="15">15 días</SelectItem>
                  <SelectItem value="30">30 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notas (opcional)</Label>
              <Textarea
                className="mt-1"
                rows={2}
                placeholder="Notas adicionales..."
                value={quoteForm.notes}
                onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setQuoteModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#1a5c4a] hover:bg-[#164a3d]"
              onClick={createQuotation}
              disabled={creatingQuote || quoteForm.items.length === 0}
            >
              {creatingQuote ? "Creando..." : "Crear Cotización"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Link de Diagnóstico */}
      <Dialog open={diagnosticoLinkModal} onOpenChange={setDiagnosticoLinkModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1a5c4a]">
              <Link className="h-5 w-5" />
              Diagnóstico Habilitado
            </DialogTitle>
            <DialogDescription>
              El formulario de diagnóstico ha sido habilitado para {selectedProspect?.full_name}. 
              Comparta el siguiente link con el cliente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <Label className="text-xs text-gray-500 mb-2 block">Link del diagnóstico:</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={diagnosticoLink}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button variant="outline" onClick={copiarLinkDiagnostico}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <p className="text-amber-800">
                <strong>Nota:</strong> Cuando el cliente complete el formulario, el diagnóstico 
                aparecerá automáticamente en la pestaña "Diagnósticos" con estado "Recibido".
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setDiagnosticoLinkModal(false)}>
              Cerrar
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={enviarLinkWhatsApp}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Enviar por WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Componente de tarjeta para el Kanban
function ProspectoCard({ prospect, onWhatsApp, onEmail, onDiagnostico, onCotizar, onChangeStatus, onDelete, userRole }) {
  return (
    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-gray-900 truncate">{prospect.full_name}</p>
          <p className="text-xs text-gray-500 truncate">{prospect.company_name || "Sin empresa"}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onDiagnostico}>
              <ClipboardCheck className="h-4 w-4 mr-2" /> Diagnóstico
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onCotizar}>
              <FileText className="h-4 w-4 mr-2" /> Cotizar
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onWhatsApp}>
              <MessageCircle className="h-4 w-4 mr-2 text-green-600" /> WhatsApp
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEmail}>
              <Mail className="h-4 w-4 mr-2" /> Email
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-xs text-gray-500" disabled>
              Mover a:
            </DropdownMenuItem>
            {Object.entries(STATUS_CONFIG).filter(([key]) => key !== prospect.status).map(([key, cfg]) => (
              <DropdownMenuItem key={key} onClick={() => onChangeStatus(key)}>
                <ArrowRight className="h-3 w-3 mr-2" /> {cfg.label}
              </DropdownMenuItem>
            ))}
            {userRole === "admin" && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDelete} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" /> Eliminar
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
        <Phone className="h-3 w-3" />
        <span className="truncate">{prospect.phone || "-"}</span>
      </div>
      
      {prospect.email && (
        <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
          <Mail className="h-3 w-3" />
          <span className="truncate">{prospect.email}</span>
        </div>
      )}
      
      <div className="flex items-center justify-between pt-2 border-t border-gray-50">
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
          {prospect.service_type || "Sin servicio"}
        </Badge>
        <span className="text-[10px] text-gray-400">
          {new Date(prospect.created_at).toLocaleDateString('es-MX')}
        </span>
      </div>
    </div>
  );
}
